import { Box, Popover, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { ChromePicker } from 'react-color';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const toSafeHex = (v) => (HEX_RE.test(v || '') ? v : '#000000');

/**
 * ColorField — MUI TextField with a ChromePicker popover.
 *
 * - Keeps internal liveColor state so the picker hex updates in real-time while
 *   dragging sliders (no parent re-render needed for smooth interaction).
 * - Shows a small colored swatch + hex text at normal TextField height.
 * - Syncs from the parent `value` prop whenever the popover is closed.
 *
 * Props:
 *   value      {string}    Hex color string (#rrggbb)
 *   onChange   {function}  Called with the new hex string: onChange('#rrggbb')
 */
export default function ColorField({ value, onChange, label, disabled, size = 'small', fullWidth, InputLabelProps }) {
    const [open, setOpen] = useState(false);
    const [liveColor, setLiveColor] = useState(() => toSafeHex(value));
    const anchorRef = useRef(null);

    // Sync from parent only when NOT actively picking (avoid clobbering mid-drag).
    useEffect(() => {
        if (!open) {
            setLiveColor(toSafeHex(value));
        }
    }, [value, open]);

    const handleChange = (color) => {
        setLiveColor(color.hex);
        onChange?.(color.hex);
    };

    return (
        <>
            <TextField
                size={size}
                label={label}
                fullWidth={fullWidth}
                disabled={disabled}
                InputLabelProps={{ shrink: true, ...InputLabelProps }}
                value={liveColor.toUpperCase()}
                inputProps={{
                    readOnly: true,
                    style: {
                        cursor: disabled ? 'default' : 'pointer',
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <Box
                            sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '3px',
                                bgcolor: liveColor,
                                border: '1px solid',
                                borderColor: 'divider',
                                flexShrink: 0,
                                mr: 0.75,
                                cursor: disabled ? 'default' : 'pointer',
                            }}
                        />
                    ),
                }}
                onClick={() => { if (!disabled) setOpen(true); }}
                inputRef={anchorRef}
            />
            <Popover
                open={open && !disabled}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableRestoreFocus
            >
                <ChromePicker
                    color={liveColor}
                    onChange={handleChange}
                    disableAlpha
                />
            </Popover>
        </>
    );
}

